import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';

import { dataLayerPush } from '@r1-frontend/analytics/dataLayerPush';

import { BaseButton, btnSizes, btnTypes } from '@r1-frontend/ui-react/components/buttons/baseButton';
import BaseCheckbox from '@r1-frontend/ui-react/components/controls/baseCheckbox';
import PopupResult, { EResultPopupStatus } from '@r1-frontend/ui-react/components/popups/popupResult';
import { CATEGORIES, EVENTS } from '@r1-frontend/shared/constants/analytics';

import PriceSelection from '~/src/features/domru-tv/packages/components/PriceSelection';
import { useConnectPackage } from '~/src/features/domru-tv/packages/hooks/useConnectPackage';
import { IPopupProps } from '~/src/features/domru-tv/packages/types';
import { ICostRa } from '~/src/store/reducers/tvPackages';

import * as ST from './styled';

const BALANCE_ERROR_CODES = ['INSUFFICIENT_CREDIT_BALANCE', 'INSUFFICIENT_DEBIT_BALANCE'];

interface IProps {
    isOpen: boolean,
    isPackageConnected: boolean,
    packetId: number,
    packageName: string,
    prices: ICostRa[],
    setResult: Dispatch<SetStateAction<Omit<IPopupProps, 'onClose'>>>,
    onClose: () => void,
}

const PackageConnectPopup = ({ isOpen, isPackageConnected, packetId, packageName, prices, setResult, onClose }: IProps): JSX.Element => {
    const isSinglePrice = prices.length === 1;
    const [selectedPrice, setSelectedPrice] = useState(prices[0]);
    const [isClosePacket, setIsClosePacket] = useState(false);
    const duration = selectedPrice?.duration;
    const raId = selectedPrice?.raId;
    const { isConnectingInProgress, onConnectPackage } = useConnectPackage({ isClosePacket, packetId, duration, raId });

    const popupTitle = useMemo(() => {
        const actionText = isPackageConnected ? 'Продлите' : 'Подключите';
        return isSinglePrice
            ? <ST.PopupTitle>Подключить пакет каналов {packageName}?</ST.PopupTitle>
            : <ST.PopupTitle>{actionText} пакет каналов {packageName} со&nbsp;скидкой</ST.PopupTitle>;
    }, [isPackageConnected, isSinglePrice, packageName]);

    const onSelectPrice = useCallback((_price) => {
        if (!_price?.duration) {
            setIsClosePacket(false);
        }
        setSelectedPrice(_price);
    }, []);

    const onConnectPackageHandler = useCallback(async() => {
        const resp = await onConnectPackage();

        const actionResultText = isPackageConnected ? 'продлили' : 'подключили';
        const actionTextByResult = resp.isSuccess ? 'success' : 'error';

        dataLayerPush({
            event: EVENTS.UAevent,
            category: CATEGORIES.vas,
            action: isPackageConnected
                ? `click_renew_subscription_${actionTextByResult}`
                : `connect ${actionTextByResult}`,
            label: `package: ${packageName} | period: ${selectedPrice?.duration} month | price: ${selectedPrice?.cost}`,
        });

        setResult({
            isOpen: true,
            title: resp.isSuccess
                ? <ST.PopupTitle>Вы {actionResultText} пакет каналов {packageName}</ST.PopupTitle>
                : <ST.PopupTitle>{resp.error.message || 'Что-то пошло не так. Попробуйте ещё раз.'}</ST.PopupTitle>,
            text: resp.isSuccess
                ? `Стоимость — ${selectedPrice?.cost} ₽/мес.`
                : BALANCE_ERROR_CODES.includes(String(resp.error.code))
                    ? 'Пожалуйста, пополните баланс перед подключением пакета каналов.'
                    : '',
            type: resp.isSuccess ? EResultPopupStatus.SUCCESS : EResultPopupStatus.ERROR,
            isBalanceError: !resp.isSuccess && BALANCE_ERROR_CODES.includes(String(resp.error.code)),
        });

        onClose();
    }, [onConnectPackage, isPackageConnected, setResult, packageName, selectedPrice?.cost, onClose]);

    return (
        <PopupResult
            isOpen={isOpen}
            title={popupTitle}
            text={isSinglePrice ? `Стоимость — ${selectedPrice?.cost} ₽/мес.` : ''}
            onClose={() => onClose()}
        >
            <ST.PopupContent>
                {!isSinglePrice && (
                    <>
                        <PriceSelection
                            selectedPrice={selectedPrice}
                            prices={prices}
                            onChange={onSelectPrice}
                            data-test='price-item'
                        />
                        <BaseCheckbox
                            checked={isClosePacket}
                            disabled={!selectedPrice?.duration}
                            onChange={() => setIsClosePacket(!isClosePacket)}
                        >
                            Отключить после окончания подписки
                        </BaseCheckbox>
                    </>
                )}
                <ST.ButtonsRow>
                    <BaseButton
                        type={btnTypes.ACCENT}
                        size={btnSizes.LARGE}
                        text={'Подключить'}
                        isLoading={isConnectingInProgress}
                        onClick={onConnectPackageHandler}
                        data-test='connect-button'
                    />
                    <BaseButton
                        type={btnTypes.SECONDARY}
                        size={btnSizes.LARGE}
                        text={'Закрыть'}
                        disabled={isConnectingInProgress}
                        onClick={() => onClose()}
                        data-test='close-button'
                    />
                </ST.ButtonsRow>
            </ST.PopupContent>
        </PopupResult>
    );
};

export default PackageConnectPopup;
